import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  ROTULO_TIPO_MARCACAO,
  temCobrancaNaAgenda,
  zBaixaDePagamento,
  type DetalheDaMarcacao,
} from '@arena/shared/schemas';
import { dataValida } from '@arena/shared/tempo';
import { agendaApi } from '../api/agenda.js';
import { ErroDeApi, ErroDeRede, mensagemDoErro } from '../api/cliente.js';
import { invalidarDinheiro } from '../api/invalidacoes.js';
import { AvisoDeCaixa } from '../financeiro/AvisoDeCaixa.jsx';
import { avisoDeDinheiroForaDoCaixa } from '../financeiro/formatos.js';
import { useCaixaAberto } from '../financeiro/useCaixaAberto.js';
import { Erro, EsqueletoDeCard } from '../ui/estados.jsx';
import { Icone } from '../ui/Icone.js';
import { CorpoDeModal, Modal } from '../ui/Modal.jsx';
import { Botao, Campo, Formulario, Mini, Nota } from '../ui/primitivos.jsx';
import { useToast } from '../ui/Toast.jsx';
import {
  dataPorExtenso,
  dinheiro,
  horaCurta,
  horaNaArena,
  paraCentavos,
  rotuloDeDuracao,
  telefoneFormatado,
  tituloDaMarcacao,
} from './formatos.js';
import { errosPorCampo, primeiraMensagem } from './ModalDeReserva.jsx';

/**
 * `#m-baixa` — dar baixa no pagamento de uma hora de quadra.
 *
 * O caminho curto entre "o pessoal jogou" e "entrou no caixa". A recepção
 * chega aqui de três lugares, e é o mesmo modal nos três: o card da
 * grade, o cartão "Horas a cobrar" do painel de hoje e a lista do
 * financeiro.
 *
 * Três campos, e cada um existe por um motivo:
 *
 *   situação   o que mudou de fato — "pago", "sinal", de volta para
 *              "a pagar" quando a baixa foi engano
 *   valor      a reserva pode ter nascido sem preço, e é na hora de
 *              cobrar que alguém sabe quanto foi
 *   data       o dia em que o dinheiro ENTROU, que não é sempre hoje: a
 *              locação de sábado recebida na segunda é caixa de segunda.
 *              É esta data que vira a data do lançamento no razão
 *
 * O que ele NÃO faz: mexer em horário, quadra ou duração. Isso é
 * "Remarcar", no modal da marcação — misturar as duas coisas num
 * formulário só é o que torna "dar baixa" uma operação que a recepção
 * tem medo de abrir.
 *
 * Numa ocorrência de série, a baixa vale SÓ PARA ELA, sem perguntar: o
 * Pix de hoje não paga as próximas onze semanas. Por isso não há aqui a
 * escolha "somente esta ou toda a série" que a remarcação faz.
 */

const OPCOES_DE_SITUACAO = [
  { valor: 'Pago', rotulo: 'Pago — recebido por inteiro' },
  { valor: 'Sinal', rotulo: 'Sinal — parte recebida' },
  { valor: 'APagar', rotulo: 'A pagar — ainda não recebeu' },
  { valor: 'Isento', rotulo: 'Isento — não se cobra' },
  { valor: 'NaMensalidade', rotulo: 'Na mensalidade — entra na fatura' },
] as const;

type SituacaoEscolhida = (typeof OPCOES_DE_SITUACAO)[number]['valor'];

const FORMAS = [
  { valor: 'Pix', rotulo: 'Pix' },
  { valor: 'Dinheiro', rotulo: 'Dinheiro' },
  { valor: 'Cartao', rotulo: 'Cartão' },
  { valor: 'Boleto', rotulo: 'Boleto' },
] as const;

/** Entrou dinheiro? É o que libera forma e data — a mesma regra do servidor. */
function recebeu(situacao: SituacaoEscolhida): boolean {
  return situacao === 'Pago' || situacao === 'Sinal';
}

export function ModalDeBaixa({
  marcacaoId,
  hoje,
  fuso,
  onFechar,
}: {
  marcacaoId: string;
  /** O hoje DA ARENA. Quem já tem a grade passa o dela; o resto, o do contexto. */
  hoje: string;
  fuso: string;
  onFechar: () => void;
}) {
  const consulta = useQuery({
    queryKey: ['agenda', 'marcacao', marcacaoId],
    queryFn: () => agendaApi.detalhe(marcacaoId),
    retry: false,
  });

  if (consulta.isPending) {
    return (
      <Modal aberto onFechar={onFechar} titulo="Dar baixa" largura="estreita">
        <CorpoDeModal>
          <EsqueletoDeCard linhas={4} />
        </CorpoDeModal>
      </Modal>
    );
  }

  if (consulta.isError) {
    const erro = consulta.error;
    return (
      <Modal aberto onFechar={onFechar} titulo="Dar baixa" largura="estreita">
        <CorpoDeModal>
          <Erro
            tipo={
              erro instanceof ErroDeRede
                ? 'rede'
                : erro instanceof ErroDeApi && erro.ehPermissao
                  ? 'permissao'
                  : 'falha'
            }
            detalhe={mensagemDoErro(erro)}
          />
        </CorpoDeModal>
      </Modal>
    );
  }

  return <BaixaDaMarcacao marcacao={consulta.data} hoje={hoje} fuso={fuso} onFechar={onFechar} />;
}

function BaixaDaMarcacao({
  marcacao: m,
  hoje,
  fuso,
  onFechar,
}: {
  marcacao: DetalheDaMarcacao;
  hoje: string;
  fuso: string;
  onFechar: () => void;
}) {
  const avisar = useToast();
  const cliente = useQueryClient();
  const caixaAberto = useCaixaAberto();

  const inicio = horaNaArena(m.inicio, fuso);
  const fim = horaNaArena(m.fim, fuso);
  const duracao = Math.round((Date.parse(m.fim) - Date.parse(m.inicio)) / 60_000);

  const [situacao, setSituacao] = useState<SituacaoEscolhida>(
    (m.situacaoPagamento as SituacaoEscolhida | null) ?? 'Pago',
  );
  const [forma, setForma] = useState<string>(m.formaPagamento ?? 'Pix');
  const [valor, setValor] = useState(
    m.valor != null ? String(m.valor / 100).replace('.', ',') : '',
  );
  const [data, setData] = useState(m.pagoEm ?? hoje);
  const [erros, setErros] = useState<Record<string, string>>({});
  const [erroGeral, setErroGeral] = useState<string | null>(null);

  // O valor em centavos como o servidor vai gravar — e o que a nota do
  // rodapé anuncia. Campo vazio é "sem preço definido", não zero.
  const centavos = valor.trim() ? paraCentavos(valor) : null;

  const baixar = useMutation({
    mutationFn: (dados: Parameters<typeof agendaApi.darBaixa>[1]) =>
      // Série: só esta ocorrência. Ver o cabeçalho deste arquivo.
      agendaApi.darBaixa(m.id, dados, m.serieId ? 'ocorrencia' : undefined),
    onSuccess: () =>
      Promise.all([
        cliente.invalidateQueries({ queryKey: ['agenda'] }),
        // O razão ganhou (ou perdeu) um lançamento: o extrato, os indicadores
        // do financeiro e o esperado do caixa estão velhos a partir de agora.
        invalidarDinheiro(cliente),
      ]),
  });

  async function confirmar() {
    setErros({});
    setErroGeral(null);

    if (valor.trim() && centavos === null) {
      setErros({ valor: 'Valor inválido — use 120 ou 120,50.' });
      return;
    }

    const pedido = zBaixaDePagamento.safeParse({
      situacaoPagamento: situacao,
      formaPagamento: recebeu(situacao) ? forma : null,
      valor: centavos,
      pagoEm: recebeu(situacao) ? data : null,
    });
    if (!pedido.success) {
      setErros(errosPorCampo(pedido.error.issues));
      return;
    }

    try {
      await baixar.mutateAsync(pedido.data);
      avisar(
        recebeu(situacao)
          ? `Baixa registrada${centavos ? ` de ${dinheiro(centavos)}` : ''}. O lançamento entrou no financeiro.`
          : 'Cobrança atualizada.',
      );
      onFechar();
    } catch (erro) {
      if (erro instanceof ErroDeApi && erro.campos) setErros(primeiraMensagem(erro.campos));
      setErroGeral(mensagemDoErro(erro));
    }
  }

  // Dar baixa em bloqueio ou em aula de turma não existe: bloqueio não
  // cobra, e turma e experimental cobram por outra porta (mensalidade e
  // site). O modal é aberto só de onde essas não aparecem, mas a tela
  // não confia nisso — ela é alcançável por URL.
  if (!temCobrancaNaAgenda(m.tipo)) {
    return (
      <Modal
        aberto
        onFechar={onFechar}
        largura="estreita"
        titulo="Esta marcação não se cobra aqui"
        rodape={<Botao onClick={onFechar}>Fechar</Botao>}
      >
        <CorpoDeModal>
          <p className="sub">
            {ROTULO_TIPO_MARCACAO[m.tipo]} não tem cobrança pela agenda.{' '}
            {m.tipo === 'TurmaFixa'
              ? 'Aula de turma entra na mensalidade do aluno — o recebimento é na tela de Planos.'
              : m.tipo === 'Experimental'
                ? 'Aula experimental é do pedido do site.'
                : 'Bloqueio é quadra fora de uso.'}
          </p>
        </CorpoDeModal>
      </Modal>
    );
  }

  const dataInvalida = recebeu(situacao) && (!dataValida(data) || data > hoje);

  return (
    <Modal
      aberto
      onFechar={onFechar}
      largura="estreita"
      marca={
        <span
          className="chip"
          style={{ background: 'var(--ok-dot)', color: 'var(--on-ac)' }}
          aria-hidden="true"
        >
          <Icone nome="money" />
        </span>
      }
      titulo="Dar baixa no pagamento"
      descricao={`${tituloDaMarcacao(m)} · ${m.quadra.nome} · ${horaCurta(inicio)} às ${horaCurta(fim)}`}
      rodape={
        <>
          <span className="left">
            {recebeu(situacao)
              ? `Entra no financeiro em ${data === hoje ? 'hoje' : dataPorExtenso(data)}`
              : 'Sem lançamento no financeiro'}
          </span>
          <Botao variante="fantasma" onClick={onFechar}>
            Cancelar
          </Botao>
          <Botao onClick={() => void confirmar()} disabled={baixar.isPending || dataInvalida}>
            {baixar.isPending ? 'Salvando…' : 'Confirmar'}
          </Botao>
        </>
      }
    >
      <CorpoDeModal>
        <Mini
          itens={[
            {
              valor: `${horaCurta(inicio)} às ${horaCurta(fim)}`,
              rotulo: rotuloDeDuracao(duracao),
            },
            { valor: m.pessoas ?? '—', rotulo: 'pessoas' },
            { valor: m.valor != null ? dinheiro(m.valor) : '—', rotulo: 'valor da reserva' },
          ]}
        />

        {m.responsavelNome && (
          <dl className="detalhes">
            <dt>Responsável</dt>
            <dd>
              {m.responsavelNome}
              {m.responsavelTelefone ? ` · ${telefoneFormatado(m.responsavelTelefone)}` : ''}
            </dd>
          </dl>
        )}

        <Formulario colunas={2} onSubmit={(e) => e.preventDefault()}>
          <Campo rotulo="Situação" erro={erros['situacaoPagamento']}>
            <select
              value={situacao}
              onChange={(e) => setSituacao(e.target.value as SituacaoEscolhida)}
            >
              {OPCOES_DE_SITUACAO.map((o) => (
                <option key={o.valor} value={o.valor}>
                  {o.rotulo}
                </option>
              ))}
            </select>
          </Campo>

          <Campo
            rotulo="Valor cobrado"
            ajuda={m.valor == null ? 'A reserva nasceu sem preço.' : undefined}
            erro={erros['valor']}
          >
            <input
              inputMode="decimal"
              placeholder="R$ 120,00"
              value={valor}
              onChange={(e) => setValor(e.target.value)}
            />
          </Campo>

          {recebeu(situacao) && (
            <>
              <Campo rotulo="Forma" erro={erros['formaPagamento']}>
                <select value={forma} onChange={(e) => setForma(e.target.value)}>
                  {FORMAS.map((f) => (
                    <option key={f.valor} value={f.valor}>
                      {f.rotulo}
                    </option>
                  ))}
                </select>
              </Campo>
              <Campo
                rotulo="Data do pagamento"
                ajuda="O dia em que o dinheiro entrou — é a data do lançamento."
                erro={
                  erros['pagoEm'] ??
                  (dataInvalida ? 'Escolha hoje ou uma data passada.' : undefined)
                }
              >
                <input
                  type="date"
                  max={hoje}
                  value={data}
                  onChange={(e) => setData(e.target.value)}
                />
              </Campo>
            </>
          )}
        </Formulario>

        {situacao === 'Pago' && avisoDeDinheiroForaDoCaixa(forma, caixaAberto) && <AvisoDeCaixa />}

        {situacao === 'Sinal' && (
          <Nota>
            O <b>sinal</b> registra que parte do valor entrou, mas a marcação guarda o preço inteiro
            — e por isso o sinal <b>não</b> vira lançamento no financeiro. A locação continua
            aparecendo em "horas a cobrar" até a baixa de "pago".
          </Nota>
        )}

        {m.serieId && (
          <Nota>
            Esta marcação faz parte de uma <b>série semanal</b>. A baixa vale{' '}
            <b>somente para esta data</b> — as outras semanas continuam a cobrar.
          </Nota>
        )}

        {erroGeral && (
          <div className="aviso conflito" role="alert">
            <Icone nome="info" />
            <div>{erroGeral}</div>
          </div>
        )}
      </CorpoDeModal>
    </Modal>
  );
}
